// File: src/components/games/GameListPage.js
import React, { useState, useEffect } from "react";
import { Container, Card, Row, Col } from "react-bootstrap";
import { Link } from "react-router-dom";
import axios from "axios";

function GameListPage() {
    const [games, setGames] = useState([]);
    const [user, setUser] = useState(null);
    const [loadingChecked, setLoadingChecked] = useState(true);

    useEffect(() => {
        axios.get('/api/users/my-details/', {
            withCredentials: true,
        })
            .then(response => {
                setUser(response.data);
            })
            .catch(error => {
                console.error('Error fetching user profile:', error);
            });
    }, []);

    // Fetch current games on component mount.
    useEffect(() => {
        axios
            .get("/api/games/current-season/", {
                withCredentials: true,
            })
            .then((response) => {
                setGames(response.data);
                // Initialize checked state (default unchecked)
                const initialChecked = {};
                response.data.forEach((game) => {
                    initialChecked[game.id] = false;
                });
                setLoadingChecked(false);
            })
            .catch((error) => {
                console.error("Error fetching games:", error);
                setLoadingChecked(false);
            });
    }, []);

    const handleAttendingToggle = async (gameId, participantId, currentAttending) => {
        const attendingUrl = `/api/games/${gameId}/attending/`;

        try {
            const response = await axios.patch(
                attendingUrl,
                { attending: !currentAttending },
                {
                    withCredentials: true,
                }
            );

            if (response.status === 200) {
                setGames((prevGames) =>
                    prevGames.map((game) =>
                        game.id === gameId
                            ? {
                                ...game,
                                participants: game.participants.map((p) =>
                                    p.id === participantId ? { ...p, attending: !currentAttending } : p
                                ),
                            }
                            : game
                    )
                );
            }
        } catch (error) {
            console.error("Error updating attending status:", error);
        }
    };

    return (
        <Container className="p-0">
            <Card className="h-100">
                {/* Card Header */}
                <Card.Header className="d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
                    <h1 className="h4 card-title">Upcoming Games</h1>
                </Card.Header>
                {/* Card Body */}
                <Card.Body>
                    <Container>
                        {loadingChecked ? (
                            <p>Loading your selections...</p>
                        ) : (
                            <Row className="g-4">
                                {games.map((game) => {
                                    // Extract participants as in your previous code
                                    const homeParticipant =
                                        game.participants?.find((p) => p.is_home && p.content_type === 'team') || {};
                                    const awayParticipant =
                                        game.participants?.find((p) => p.is_away && p.content_type === 'team') || {};
                                    const userParticipant = game.participants?.find(
                                        (p) => p.content_type === "player" && user?.players?.some(player => player.id === p.object_id)
                                    ) || {};

                                    return (
                                        <Col key={game.id} className="col-sm-6 col-xl-4">
                                            <Card className="h-100">
                                                <div className="position-relative">
                                                    {/* 50×50 Image Composition */}
                                                    <div
                                                        style={{
                                                            width: '200px',
                                                            height: '200px',
                                                            margin: 'auto',
                                                            display: 'block',
                                                            position: 'relative',
                                                            overflow: 'hidden',
                                                            borderRadius: '8px',
                                                        }}
                                                    >
                                                        {homeParticipant.participant_logo ? (
                                                            // Top half – Home Team logo
                                                            <img
                                                                src={homeParticipant.participant_logo}
                                                                alt={`${homeParticipant.participant_name} logo`}
                                                                style={{
                                                                    width: '200px',
                                                                    height: '200px',
                                                                    objectFit: 'cover',
                                                                    position: 'absolute',
                                                                    left: 0, // Ensure image is aligned to the left
                                                                    top: 0,
                                                                    clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                                                                }}
                                                            />
                                                        ) : (
                                                            // Placeholder for top half – Blue
                                                            <div
                                                                style={{
                                                                    width: '100%',
                                                                    height: '50%',
                                                                    backgroundColor: 'blue',
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                }}
                                                            ></div>
                                                        )}
                                                        {awayParticipant.participant_logo ? (
                                                            // Bottom half – Away Team logo
                                                            <img
                                                                src={awayParticipant.participant_logo}
                                                                alt={`${awayParticipant.participant_name} logo`}
                                                                style={{
                                                                    width: '200px',
                                                                    height: '200px',
                                                                    objectFit: 'cover',
                                                                    position: 'absolute',
                                                                    left: 0, // Ensure image is aligned to the left
                                                                    bottom: 0,
                                                                    clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                                                                }}
                                                            />
                                                        ) : (
                                                            // Placeholder for bottom half – Red
                                                            <div
                                                                style={{
                                                                    width: "100%",
                                                                    height: "100%",
                                                                    backgroundColor: "red",
                                                                    position: "absolute",
                                                                    bottom: 0,
                                                                }}
                                                            ></div>
                                                        )}
                                                    </div>
                                                    <div className="badge bg-danger text-white mt-2 me-2 position-absolute top-0 end-0">
                                                        Upcoming
                                                    </div>
                                                </div>
                                                <Card.Body className="position-relative pt-0">
                                                    <div className="btn btn-xs btn-primary mt-n3">
                                                        {game.league_name}
                                                    </div>
                                                    <h6 className="mt-3">
                                                        <Link className="nav-link" to={`/games/${game.id}`}>
                                                            {game.home_team} vs. {game.away_team}
                                                        </Link>
                                                    </h6>
                                                    <p className="mb-0 small">
                                                        <i className="bi bi-calendar-check pe-1"></i>{" "}
                                                        {game.scheduled_date_only} at{" "}
                                                        {game.scheduled_time_only}
                                                    </p>
                                                    <p className="small">
                                                        <i className="bi bi-geo-alt pe-1"></i>{" "}
                                                        {game.location?.name}
                                                    </p>
                                                    <ul className="avatar-group list-unstyled align-items-center mb-0">
                                                        {(() => {
                                                            const attendingPlayers = game.participants
                                                                .filter(p => p.content_type === 'player' && p.attending);

                                                            const displayed = attendingPlayers.slice(0, 3);
                                                            const remaining = attendingPlayers.length - displayed.length;

                                                            return (
                                                                <>
                                                                    {displayed.map((p, idx) => (
                                                                        <li key={`${p.id}-${idx}`} className="avatar avatar-xs">
                                                                            <img
                                                                                className="avatar-img rounded-circle"
                                                                                src={
                                                                                    p.participant_logo ||
                                                                                    `${process.env.PUBLIC_URL}/images/kb_static/KLOC_circle.svg`
                                                                                }
                                                                                alt={p.participant_name}
                                                                            />
                                                                        </li>
                                                                    ))}

                                                                    {remaining > 0 && (
                                                                        <li className="avatar avatar-xs">
                                                                            <div className="avatar-img rounded-circle bg-primary">
                                                                                <span className="smaller text-white position-absolute top-50 start-50 translate-middle">
                                                                                    +{remaining}
                                                                                </span>
                                                                            </div>
                                                                        </li>
                                                                    )}

                                                                    <li className="ms-3">
                                                                        <small>
                                                                            {attendingPlayers.length}{' '}
                                                                            {attendingPlayers.length === 1 ? 'attending' : 'attending'}
                                                                        </small>
                                                                    </li>
                                                                </>
                                                            );
                                                        })()}
                                                    </ul>
                                                    <div className="d-flex mt-3 justify-content-between">
                                                        {/* Interested Button */}
                                                        <div className="w-100">
                                                            <input
                                                                type="checkbox"
                                                                className="btn-check"
                                                                id={`Interested-${userParticipant.id}`}  // Use participant ID
                                                                checked={userParticipant.attending || false}  // Default to false if undefined
                                                                onChange={() => handleAttendingToggle(game.id, userParticipant.id, userParticipant.attending)}
                                                            />
                                                            <label
                                                                className={`btn btn-sm ${userParticipant.attending ? 'btn-success' : 'btn-outline-success'}`}
                                                                htmlFor={`Interested-${userParticipant.id}`}
                                                            >
                                                                <i className="fa-solid fa-thumbs-up me-1"></i> {userParticipant.attending ? "Attending" : "Not Attending"}
                                                            </label>
                                                        </div>
                                                        <div className="dropdown ms-3">
                                                            <a
                                                                href="#!"
                                                                className="btn btn-sm btn-primary-soft"
                                                                id="gameActionShare"
                                                                data-bs-toggle="dropdown"
                                                                aria-expanded="false"
                                                            >
                                                                <i className="bi bi-share-fill"></i>
                                                            </a>
                                                            <ul
                                                                className="dropdown-menu dropdown-menu-end"
                                                                aria-labelledby="gameActionShare"
                                                            >
                                                                <li>
                                                                    <a className="dropdown-item" href="#!">
                                                                        <i className="bi bi-envelope fa-fw pe-1"></i>{" "}
                                                                        Send via Direct Message
                                                                    </a>
                                                                </li>
                                                                <li>
                                                                    <a className="dropdown-item" href="#!">
                                                                        <i className="bi bi-bookmark-check fa-fw pe-1"></i>{" "}
                                                                        Share to News Feed
                                                                    </a>
                                                                </li>
                                                                <li>
                                                                    <a className="dropdown-item" href="#!">
                                                                        <i className="bi bi-people fa-fw pe-1"></i>{" "}
                                                                        Share to a group
                                                                    </a>
                                                                </li>
                                                                <li>
                                                                    <a className="dropdown-item" href="#!">
                                                                        <i className="bi bi-share fa-fw pe-1"></i>{" "}
                                                                        Share post via …
                                                                    </a>
                                                                </li>
                                                                <li>
                                                                    <hr className="dropdown-divider" />
                                                                </li>
                                                                <li>
                                                                    <a className="dropdown-item" href="#!">
                                                                        <i className="bi bi-person fa-fw pe-1"></i>{" "}
                                                                        Share on a friend's profile
                                                                    </a>
                                                                </li>
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        )}
                    </Container>
                </Card.Body>
            </Card>
        </Container>
    );
}

export default GameListPage;
